// src/components/TrackerList.tsx
import { useMemo } from 'react';
import { useTrackerStore } from '../stores/trackerStore';
import { ParlayCard } from './ParlayCard';
import { PickStatus, TrackerEntry } from '../domain/types';
import { FiCheckCircle, FiXCircle, FiClock, FiMinusCircle, FiTrash2 } from 'react-icons/fi';

const PICK_STATUS_CONFIG: Record<PickStatus, { label: string; color: string; icon: React.ReactNode }> = {
  [PickStatus.PENDING]: { label: 'Pendente', color: '#B7FF00', icon: <FiClock size={12} /> },
  [PickStatus.WON]: { label: 'Won', color: '#22C55E', icon: <FiCheckCircle size={12} /> },
  [PickStatus.LOST]: { label: 'Lost', color: '#EF4444', icon: <FiXCircle size={12} /> },
  [PickStatus.PUSH]: { label: 'Push', color: '#888', icon: <FiMinusCircle size={12} /> },
  [PickStatus.VOID]: { label: 'Void', color: '#888', icon: <FiXCircle size={12} /> },
};

const SPORT_COLOR = { NBA: '#B7FF00', NHL: '#B7FF00' } as const;

export function TrackerList() {
  const { filteredEntries, filter, setFilter, load, removeEntry, stats } = useTrackerStore();

  const tabs: { id: typeof filter; label: string }[] = [
    { id: '7days', label: '7 dias' },
    { id: '30days', label: '30 dias' },
    { id: 'all', label: 'Tudo' },
    { id: 'today', label: new Date().toLocaleDateString('pt-BR', {day:'2-digit',month:'short'}) },
  ];

  // separa picks e parlays
  const picks = useMemo(() => filteredEntries.filter(e => e.type === 'pick'), [filteredEntries]);
  const parlays = useMemo(() => filteredEntries.filter(e => e.type === 'parlay'), [filteredEntries]);

  return (
    <div style={{ padding: '16px', paddingBottom: 100 }}>
      {/* Header com stats rápidas */}
      {/* Tabs de filtro */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
        {tabs.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setFilter(id)}
            style={{
              padding: '8px 16px',
              background: filter === id ? 'rgba(183,255,0,0.16)' : 'rgba(255,255,255,0.03)',
              border: filter === id ? '1px solid rgba(183,255,0,0.55)' : '1px solid rgba(255,255,255,0.06)',
              borderRadius: 20,
              color: filter === id ? '#B7FF00' : '#888',
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              transition: 'all 0.2s',
            }}
          >
            {label} {filteredEntries.length > 0 && filter === id && (
              <span style={{ background: '#B7FF00', color: '#000', borderRadius: 10, padding: '0 6px', fontSize: 10, marginLeft: 4 }}>
                {filteredEntries.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Stats resumo */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
        <StatCard title="Picks" value={`${stats.won}/${stats.lost}/${stats.push}`} subtitle={`${stats.won + stats.lost + stats.push} total`} color="#B7FF00" />
        <StatCard title="Parlays" value={`${stats.green}/${stats.red}/${stats.void}`} subtitle={`${stats.green + stats.red + stats.void} total`} color="#B7FF00" />
        <StatCard title="Hit Rate" value={`${stats.won + stats.lost > 0 ? Math.round((stats.won / (stats.won + stats.lost)) * 100) : 0}%`} subtitle="Picks resolvidos" color="#B7FF00" />
      </div>

      {/* Lista */}
      {filteredEntries.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#888', padding: 48 }}>
          Nenhuma entrada encontrada
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {picks.length > 0 && (
            <>
              <h4 style={{ margin: '0 0 8px', fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Picks Individuais</h4>
              {picks.map(entry => (
                <PickCard key={entry.data.id} entry={entry} onDelete={() => removeEntry(entry.data.id)} />
              ))}
            </>
          )}
          {parlays.length > 0 && (
            <>
              <h4 style={{ margin: '16px 0 8px', fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Parlays</h4>
              {parlays.map(entry => (
                <ParlayCard key={entry.data.id} parlay={entry.data} onDelete={() => removeEntry(entry.data.id)} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PickCard({ entry, onDelete }: { entry: TrackerEntry & { type: 'pick' }; onDelete: () => void }) {
  const { data } = entry;
  const status = PICK_STATUS_CONFIG[data.status];
  const sportColor = SPORT_COLOR[data.sport];

  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '12px', background: 'rgba(255,255,255,0.02)',
      border: `1px solid ${data.status === PickStatus.PENDING ? 'rgba(234,179,8,0.3)' : 'rgba(255,255,255,0.06)'}`,
      borderRadius: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: `linear-gradient(135deg, ${sportColor}22, ${sportColor}11)`,
          border: `1px solid ${sportColor}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 14, color: sportColor, flexShrink: 0,
        }}>{data.sport === 'NBA' ? '🏀' : '🏒'}</div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {data.player}
            </span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
              background: `${status.color}22`, color: status.color, border: `1px solid ${status.color}44`,
            }}>{status.icon} {status.label}</span>
          </div>
          <div style={{ fontSize: 11, color: '#888', display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 2 }}>
            <span>{data.market} • Linha {data.line}</span>
            <span style={{ color: data.side === 'over' ? '#22C55E' : '#EF4444', fontWeight: 600 }}>
              {data.side.toUpperCase()} @{data.odds.toFixed(2)}
            </span>
            {data.hitRatePct && <span style={{ color: data.hitRatePct >= 60 ? '#22C55E' : '#888' }}>HR {data.hitRatePct}%</span>}
            <span>R$ {data.stake.toFixed(2)}</span>
          </div>
          {data.resultValue !== undefined && (
            <div style={{ fontSize: 11, marginTop: 4, color: data.status === PickStatus.WON ? '#22C55E' : data.status === PickStatus.LOST ? '#EF4444' : '#888' }}>
              Resultado: {data.resultValue} {data.status === PickStatus.PUSH ? '(Push)' : ''}
            </div>
          )}
        </div>
      </div>
      <button onClick={onDelete} style={{ padding: 8, color: '#555', background: 'none', border: 'none', borderRadius: 8 }}>
        <FiTrash2 size={18} />
      </button>
    </div>
  );
}

function StatCard({ title, value, subtitle, color }: { title: string; value: string; subtitle?: string; color: string }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)',
      border: `1px solid ${color}22`,
      borderRadius: 12,
      padding: '12px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color, margin: '4px 0' }}>{value}</div>
      {subtitle && <div style={{ fontSize: 10, color: '#666' }}>{subtitle}</div>}
    </div>
  );
}
